import { useRollCompetitionEntriesMeByCycleId } from '@/api/roll-competition-entries';
import { useRollCompetitions } from '@/api/roll-competitions';
import { ModalWithVersion } from '@/components/core/Modal/WithVersion';
import { ModalsKey } from '@/enums/modalsKey';
import { useRollPointSpinButton } from '@/hooks/roll-point/useRollPointSpinButton';
import { RootState } from '@/lib/store';
import { Button, Image } from '@starsoft/common/components';
import useTranslation from 'next-translate/useTranslation';
import { PropsWithChildren, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styles from './styles.module.scss';

export default function RollPointGameLayout({ children }: PropsWithChildren) {
  const { t } = useTranslation('common');
  const isSpinning = useSelector((state: RootState) => state.rollPoint.animate);
  const {
    spin,
    isPending: pendingRoll,
    useRollCompetitionEntriesValidateQuery: {
      canCreateRollCompetitionEntry,
      isPending,
    },
  } = useRollPointSpinButton();

  const { rollCompetition } = useRollCompetitions();
  useRollCompetitionEntriesMeByCycleId({
    cycleId: rollCompetition?.cycle?.id,
  });

  const label = useMemo(() => {
    if (canCreateRollCompetitionEntry?.noRollCompetitionCycle) {
      return t('roll_competition_no_cycle');
    }

    if (canCreateRollCompetitionEntry?.noDeposit) {
      return t('roll_competition_no_deposit');
    }

    if (canCreateRollCompetitionEntry?.hasExistingEntry) {
      return t('roll_competition_has_existing_entry');
    }

    return t('roll');
  }, [t, canCreateRollCompetitionEntry]);

  const isDisabled =
    isSpinning ||
    canCreateRollCompetitionEntry?.hasExistingEntry ||
    canCreateRollCompetitionEntry?.noRollCompetitionCycle ||
    pendingRoll;

  return (
    <ModalWithVersion
      modal={ModalsKey.RollCompetition}
      title={t('roll_competition_title')}
    >
      <div className={styles.container}>
        <div className={styles.container__content}>{children}</div>
        <Button
          isTransparent={process.env.NEXT_PUBLIC_TENANT_ID !== 'kube'}
          type="button"
          isSkeleton={isPending}
          onClick={spin}
          disabled={isDisabled}
        >
          <span
            className={`${styles.container__button__text} ${
              process.env.NEXT_PUBLIC_TENANT_ID === 'kube'
                ? styles['container__button__text--kube']
                : ''
            }`}
          >
            {label}
          </span>
          {process.env.NEXT_PUBLIC_TENANT_ID !== 'kube' && (
            <Image
              className={`${styles.container__button__center} ${isDisabled ? styles['container__button__center--disabled'] : ''}`}
              src={`${process.env.NEXT_PUBLIC_S3_BUCKET_BASE_URL}/artworks/roll-competition/button.png`}
              alt="button"
              bundle
            />
          )}
        </Button>
      </div>
    </ModalWithVersion>
  );
}
